import React, { useEffect, useMemo, useState } from "react";
import { loadMaterialList, loadMaterial } from "../features/materials/materialData";
import { duplicateCutSheet, duplicateMaterialChoices, suggestDuplicateName } from "../features/jobs/cutSheetSwitch";

/**
 * Copy a cut sheet onto another material (2026-10-21): the same nest (parts at the same quantity per
 * sheet), image, cut times, estimate and customer, on substitute steel, so a job can switch to it
 * (CutSheetSwitchModal). POST /cut_sheets/{id}/duplicate; the API copies the SAVED sheet and leaves
 * out a stock drop (a piece of the old material).
 *
 * Used inside the switch dialog and by DuplicateCutSheetModal on the Cut Sheet page. `onCreated(data)`
 * gets the API's answer ({cut_sheet_id, cut_sheet_name, material_code, warnings}).
 */
export default function DuplicateCutSheetForm({ cutSheetId, cutSheetName, materialId, onCreated, onCancel }) {
  const [materials, setMaterials] = useState(null);
  const [source, setSource] = useState(null);
  const [search, setSearch] = useState("");
  const [anySize, setAnySize] = useState(false);
  const [picked, setPicked] = useState("");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [all, src] = await Promise.all([
          loadMaterialList(),
          materialId ? loadMaterial(materialId).catch(() => null) : Promise.resolve(null),
        ]);
        if (!live) return;
        setMaterials(Array.isArray(all) ? all : []);
        setSource(src || (Array.isArray(all) ? all.find((m) => m.material_id === Number(materialId)) : null) || null);
      } catch (e) {
        if (live) setError(e.message || "Could not load the materials.");
      }
    })();
    return () => { live = false; };
  }, [materialId]);

  const choices = useMemo(() => duplicateMaterialChoices(materials, source, { search, anySize }),
    [materials, source, search, anySize]);
  const pickedRow = useMemo(() => (materials || []).find((m) => String(m.material_id) === String(picked)) || null,
    [materials, picked]);

  useEffect(() => {
    if (!nameTouched && pickedRow) setName(suggestDuplicateName(cutSheetName, source?.material_code, pickedRow.material_code));
  }, [pickedRow, nameTouched, cutSheetName, source]);

  const submit = async () => {
    if (!picked || !name.trim()) return;
    setBusy(true);
    setError("");
    const r = await duplicateCutSheet(cutSheetId, { materialId: picked, name: name.trim(), notes: notes.trim() || null });
    setBusy(false);
    if (!r.ok) {
      setError(r.message);
      return;
    }
    if (onCreated) onCreated(r.data);
  };

  return (
    <div className="border rounded p-2 bg-light" data-testid="duplicate-cut-sheet-form">
      <div className="fw-semibold mb-1">Copy {cutSheetName || `cut sheet #${cutSheetId}`} onto another material</div>
      <div className="small text-muted mb-2">
        Same nest, image, cut times and customer. The saved sheet is copied; a stock drop is not (set it on the
        copy if the new steel leaves one).
        {source ? <> Now on <span className="font-monospace">{source.material_code}</span>.</> : null}
      </div>
      {error && <div className="alert alert-danger py-1 small mb-2">{error}</div>}
      {materials === null && !error ? (
        <div className="small text-muted">Loading materials…</div>
      ) : (
        <>
          <div className="d-flex gap-2 align-items-center mb-2">
            <input className="form-control form-control-sm" placeholder="Search material code or description"
              aria-label="Search materials" value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="form-check text-nowrap">
              <input className="form-check-input" type="checkbox" id="dupAnySize" checked={anySize}
                onChange={(e) => setAnySize(e.target.checked)} />
              <label className="form-check-label small" htmlFor="dupAnySize">Any sheet size</label>
            </div>
          </div>
          <select className="form-select form-select-sm mb-2" aria-label="New material" value={picked}
            onChange={(e) => setPicked(e.target.value)}>
            <option value="">— pick the material to cut it from ({choices.length}) —</option>
            {choices.slice(0, 200).map((m) => (
              <option key={m.material_id} value={m.material_id}>
                {m.material_code} · {m.description} · {Number(m.stock_quantity) || 0} on hand
              </option>
            ))}
          </select>
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <label className="form-label small mb-0" htmlFor="dupName">New cut sheet name</label>
              <input id="dupName" className="form-control form-control-sm font-monospace" value={name}
                onChange={(e) => { setName(e.target.value); setNameTouched(true); }} />
            </div>
            <div className="col-md-6">
              <label className="form-label small mb-0" htmlFor="dupNotes">Note (optional)</label>
              <input id="dupNotes" className="form-control form-control-sm" value={notes}
                placeholder="e.g. for SO-1144: Ryerson HRPO late" onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            {onCancel && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel} disabled={busy}>Cancel</button>}
            <button type="button" className="btn btn-sm btn-primary" onClick={submit} disabled={busy || !picked || !name.trim()}>
              {busy ? "Copying…" : "Make the copy"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** The form in a dialog, for the Cut Sheet page. */
export function DuplicateCutSheetModal({ show, onHide, cutSheetId, cutSheetName, materialId, onCreated }) {
  const [done, setDone] = useState(null);
  useEffect(() => { if (!show) setDone(null); }, [show]);
  if (!show) return null;
  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} role="dialog"
      aria-label="Copy onto another material">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h5 className="modal-title">Copy onto another material</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onHide}></button>
          </div>
          <div className="modal-body">
            {done ? (
              <div className="alert alert-success mb-0">
                Made <span className="font-monospace fw-semibold">{done.cut_sheet_name}</span> (#{done.cut_sheet_id}) on{" "}
                <span className="font-monospace">{done.material_code}</span>.
                {(done.warnings || []).map((w) => <div key={w} className="small text-warning-emphasis mt-1">{w}</div>)}
              </div>
            ) : (
              <DuplicateCutSheetForm cutSheetId={cutSheetId} cutSheetName={cutSheetName} materialId={materialId}
                onCancel={onHide} onCreated={(d) => setDone(d)} />
            )}
          </div>
          {done && (
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onHide}>Close</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => { if (onCreated) onCreated(done); onHide(); }}>
                Open the copy
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
